import {
  SDL_HEADER_SELECTOR,
  SDL_HEADER_SELECTOR_APC,
  SDL_REDESIGN_HEADER_SELECTOR,
  TAB_BAR_HEIGHT,
  scrollBehavior, SLASH, Protocol } from './constants';
import type { TScrollToComponentOptions, TryParseReturn, TryStringifyReturn } from './types/common';

declare global {
  interface Window {
    bvKey?: string;
    isV3?: boolean;
    isAsco?: boolean;
    isApc?: boolean;
    cartVersion?: CartVersion;
    isReplatformedHmc?: boolean;
    isPublicPes?: boolean;
  }
}

// Used for deciding which product-cart page to show. The one using ATG or the one using Spryker
export enum CartVersion {
  'v3' = 'v3',
  'v2' = 'v2',
}

export const isBrowser = () => typeof window !== 'undefined';
export const isServer = () => !isBrowser();
export const isV3 = () => Boolean(window.isV3);
export const isPublicEnvironment = (isExecutedInBrowser = isBrowser): boolean => {
  if (!isExecutedInBrowser()) return false;

  if (window.isPublicPes) return true;
  try {
    return document.querySelector('body').getAttribute('is-public') === 'true';
  } catch (error) {
    console.warn('Could not check if code is executed from Public Environment', error);
    return false;
  }
};
export const useReplatformedHMC = () => Boolean(window.isReplatformedHmc);
export const productCartPageVersion: CartVersion = window.cartVersion;

type BuildURLOptions = {
  host?: string;
  contextRoot?: string;
  countryCode?: string;
  languageCode?: string;
  endpoint: string;
};

export const getBrandName = () => {
  if (window.isAsco) return 'ascopower';
  if (window.isApc) return 'apc';
  return 'se';
};

export const buildURL = ({
  host,
  contextRoot,
  countryCode,
  languageCode,
  endpoint,
}: BuildURLOptions): string => {
  let path = endpoint;
  if (languageCode) {
    path = `/${languageCode}${path}`;
  }

  if (countryCode) {
    /**
     * countryCode for China is
     * - lowercase (cn) if China-specific short-domain url is used as a host: 'https://schneider-electric.'
     *   which should be concatenated with countryCode without '/'
     * - uppercase (CN) if RP urls are used - in this case path should start with '/' symbol
     * Other countries' paths start with '/' symbol
     */
    path = `${countryCode !== 'cn' ? '/' : ''}${countryCode}${path}`;
  }

  if (contextRoot) {
    path = `${contextRoot}${path}`;
  }

  if (host) {
    path = `${host}${path}`;
  }

  return path;
};

export const getSDLHeaderSelector = (): string => {
  if (isV3()) return SDL_REDESIGN_HEADER_SELECTOR;

  // @ts-ignore
  if (window.isApc) {
    return SDL_HEADER_SELECTOR_APC;
  }
  return SDL_HEADER_SELECTOR;
};

export const getHeaderHeight = () => (
  document.querySelector(getSDLHeaderSelector())?.clientHeight ?? 0
);

export const scrollToComponent = (
  el: HTMLElement,
  options: TScrollToComponentOptions = {},
): void => {
  const { behavior = scrollBehavior.AUTO, offset = TAB_BAR_HEIGHT } = options;

  // A quick and dirty solution to the issue with scrolling to a component on sites of pes-catalog consumers:
  //  - MySe
  //  - MySchneider
  // See https://jira.se.com/browse/PESV3-27234 for details.
  if (document.querySelector(getSDLHeaderSelector())) {
    //  If there is the SDL header (we are on se.com or apc.com), we can scroll to the component while taking into account the header and the tabbar heights.
    const top = el.getBoundingClientRect().y + window.scrollY - getHeaderHeight() - offset;

    window.scrollTo({ top, behavior });
  } else {
    // If there is no header, we don't even try to guess the height of a consumer's header and just scroll the component into view.
    //  Most likely, it will be partially covered by the header, but we decided not to care about that for now.
    el.scrollIntoView();
  }
};

export const consoleErrorMsg = (err, origin: string) => {
  console.error(`Error in ${origin}`, err);
};

export const mergeUrlWithHost = (host: string, url: string): string => {
  if (host?.endsWith(SLASH) && url.startsWith(SLASH)) {
    host.slice(0, host.length);
  }
  return `${host}${url}`;
};

export const hasHttpOrHttps = (host: string) => {
  return host.includes(`${Protocol.HTTP}://`) || host.includes(`${Protocol.HTTPS}://`);
};

export const validateHost = (host: string) => {
  return hasHttpOrHttps(host) ? host : '';
};

export function tryParse(data: string): TryParseReturn {
  try {
    return { result: JSON.parse(data), error: null };
  } catch (error) {
    return { result: null, error: error as Error };
  }
}

export function tryStringify(data: object): TryStringifyReturn {
  try {
    return { result: JSON.stringify(data), error: null };
  } catch (error) {
    return { result: null, error: error as Error };
  }
}
